import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Chip,
} from "@mui/material";
import api from "../../utils/api";
import UserActions from "./UserActions";
import UserFormDialog from "./UserFormDialog";
import UserDeleteDialog from "./UserDeleteDialog";
import BulkInviteButton from "./BulkInviteButton";
import UserResetPasswordDialog from "./UserResetPasswordDialog";
import UserResetSuccessDialog from "./UserResetSuccessDialog";

const AllUsersTable = () => {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState("add");
  const [formData, setFormData] = useState({});
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [userToReset, setUserToReset] = useState(null);
  const [resetResult, setResetResult] = useState(null);

  useEffect(() => {
    loadUsers();
    loadRoles();
  }, []);

  useEffect(() => {
    setFilteredUsers(
      users.filter((user) =>
        ["Username", "Name", "Lastname", "Email"]
          .map((key) => user[key]?.toLowerCase())
          .some((value) => value && value.includes(searchTerm.toLowerCase()))
      )
    );
  }, [users, searchTerm]);

  const loadUsers = async () => {
    try {
      const response = await api.get("/users-by-role");

      const userMap = new Map();
      response.data.forEach((row) => {
        if (!userMap.has(row.Username)) {
          userMap.set(row.Username, {
            Username: row.Username,
            UserID: row.UserID,
            Name: row.Name,
            Lastname: row.Lastname,
            Email: row.Email,
            Status: row.status || row.Status,
            AssociatedAccounts: Array.isArray(row.AssociatedAccounts)
              ? row.AssociatedAccounts
              : row.AssociatedAccounts
              ? String(row.AssociatedAccounts)
                  .split(",")
                  .map((a) => a.trim())
              : [],
            Roles: [],
          });
        }

        userMap.get(row.Username).Roles.push({
          RoleID: row.RoleID,
          RoleName: row.RoleName,
        });
      });

      const grouped = Array.from(userMap.values());
      const withValidRoles = grouped.filter((user) =>
        user.Roles.some((r) => r.RoleID !== 3)
      );
      const activeUsers = withValidRoles.filter(
        (user) => user.Status?.toLowerCase() !== "inactive"
      );

      setUsers(activeUsers);
    } catch (err) {
      console.error("Failed to fetch users", err);
    }
  };

  const loadRoles = async () => {
    try {
      const response = await api.get("/roles");
      setAvailableRoles(response.data);
    } catch (err) {
      console.error("Failed to fetch roles", err);
    }
  };

  const handleEditUser = (user) => {
    setFormData({
      ...user,
      Roles: user.Roles?.map((r) => r.RoleID) || [],
    });
    setFormMode("edit");
    setShowForm(true);
  };

  const handleSubmitUser = async (submittedData) => {
  try {
    if (formMode === "edit") {
      await api.put(`/users/${submittedData.UserID}`, submittedData);
    } else {
      if (!submittedData.Password || !submittedData.Username || !submittedData.Roles?.length) {
        alert("Username, password, and at least one role are required.");
        return;
      }

      await api.post("/addUser", {
        username: submittedData.Username,
        password: submittedData.Password,
        email: submittedData.Email || "default@example.com",
        name: submittedData.Name || "",
        lastname: submittedData.Lastname || "",
        roles: submittedData.Roles,
        sendInvitationEmail: submittedData.SendInvite || false,
        AssociatedAccounts: submittedData.AssociatedAccounts || [],
      });
    }

    setShowForm(false);
    loadUsers();
  } catch (error) {
    console.error("Error submitting user:", error);
    alert("Failed to submit user.");
  }
};

  const handleResetPassword = (username) => {
    setUserToReset(username);
    setShowResetDialog(true);
  };

  const confirmResetPassword = async () => {
    try {
      const res = await api.post(`/users/${userToReset}/resetPassword`);
      setResetResult({
        username: userToReset,
        password: res.data.message?.split(": ")[1],
      });
    } catch (err) {
      console.error("Failed to reset password:", err);
      alert("Failed to reset password.");
    } finally {
      setShowResetDialog(false);
    }
  };

  const handleDeleteUser = (username) => {
    setUserToDelete(username);
    setShowDeleteDialog(true);
  };

  const handleSelectUser = (username) => {
    setSelectedUsers((prev) =>
      prev.includes(username)
        ? prev.filter((u) => u !== username)
        : [...prev, username]
    );
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allUsernames = filteredUsers.map((user) => user.Username);
      setSelectedUsers(allUsernames);
    } else {
      setSelectedUsers([]);
    }
  };

  const isAllSelected =
    filteredUsers.length > 0 &&
    filteredUsers.every((user) => selectedUsers.includes(user.Username));

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.96)",
      borderRadius: "10px",
    },
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.25,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const getStatusChip = (status) => {
    const isActive = status?.toLowerCase() === "active";

    return (
      <Chip
        label={status || "Unknown"}
        size="small"
        sx={{
          borderRadius: "8px",
          fontWeight: 700,
          backgroundColor: isActive
            ? "rgba(46,125,50,0.10)"
            : "rgba(211,47,47,0.10)",
          color: isActive ? "#2e7d32" : "#d32f2f",
        }}
      />
    );
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", md: "center" },
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: "#223142",
              mb: 0.5,
            }}
          >
            Users
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: "#6b778c",
            }}
          >
            Search, manage, invite, and maintain user accounts.
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            flexWrap: "wrap",
            width: { xs: "100%", md: "auto" },
          }}
        >
          <TextField
            label="Search Users"
            size="small"
            fullWidth
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              ...fieldSx,
              minWidth: { xs: "100%", md: 280 },
              maxWidth: { xs: "100%", md: 380 },
            }}
          />

          <Button
            variant="contained"
            sx={primaryButtonSx}
            onClick={() => {
              setFormData({
                Username: "",
                Password: "",
                Name: "",
                Lastname: "",
                Email: "",
                Roles: [],
                SendInvite: true,
              });
              setFormMode("add");
              setShowForm(true);
            }}
          >
            New User
          </Button>

          <BulkInviteButton
            selectedUsers={selectedUsers}
            onSuccess={() => console.log("Invites sent!")}
          />
        </Box>
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          display: { xs: "none", lg: "block" },
          maxHeight: "calc(100vh - 250px)",
          width: "100%",
          overflowX: "auto",
          borderRadius: "16px",
          border: "1px solid rgba(47,99,173,0.08)",
          boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
          backgroundColor: "rgba(255,255,255,0.96)",
        }}
      >
        <Table
          stickyHeader
          size="small"
          sx={{
            minWidth: "max-content",
            width: "100%",
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                padding="checkbox"
                sx={{
                  backgroundColor: "rgba(47,99,173,0.04)",
                  width: 50,
                  borderBottom: "1px solid rgba(47,99,173,0.08)",
                }}
              >
                <Checkbox
                  checked={isAllSelected}
                  onChange={handleSelectAll}
                  sx={{ p: 0.5 }}
                />
              </TableCell>

              {[
                "Username",
                "Name",
                "Lastname",
                "Email",
                "Associated Accounts",
                "Roles",
                "Status",
                "Actions",
              ].map((col) => (
                <TableCell
                  key={col}
                  sx={{
                    fontWeight: 700,
                    backgroundColor: "rgba(47,99,173,0.04)",
                    color: "#223142",
                    whiteSpace: "nowrap",
                    borderBottom: "1px solid rgba(47,99,173,0.08)",
                  }}
                >
                  {col}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredUsers.length > 0 ? (
              filteredUsers.map((user) => (
                <TableRow key={user.Username} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedUsers.includes(user.Username)}
                      onChange={() => handleSelectUser(user.Username)}
                      color="primary"
                      sx={{ p: 0.5 }}
                    />
                  </TableCell>

                  <TableCell>{user.Username}</TableCell>
                  <TableCell>{user.Name}</TableCell>
                  <TableCell>{user.Lastname}</TableCell>

                  <TableCell
                    sx={{
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      overflowWrap: "anywhere",
                      maxWidth: 240,
                    }}
                  >
                    {user.Email}
                  </TableCell>

                  <TableCell
                    sx={{
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      overflowWrap: "anywhere",
                      maxWidth: 220,
                    }}
                  >
                    {user.AssociatedAccounts?.join(", ") || "None"}
                  </TableCell>

                  <TableCell
                    sx={{
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      overflowWrap: "anywhere",
                      maxWidth: 220,
                    }}
                  >
                    {user.Roles?.map((r) => r.RoleName).join(", ") || "No Roles"}
                  </TableCell>

                  <TableCell>{getStatusChip(user.Status)}</TableCell>

                  <TableCell>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <UserActions
                        user={user}
                        onEdit={handleEditUser}
                        onResetPassword={handleResetPassword}
                        onDelete={handleDeleteUser}
                      />
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={9} sx={{ color: "text.secondary" }}>
                  No users found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: { xs: "block", lg: "none" } }}>
        {filteredUsers.length > 0 ? (
          filteredUsers.map((user) => (
            <Paper
              key={user.Username}
              sx={{
                p: 2,
                mb: 2,
                borderRadius: "16px",
                border: "1px solid rgba(47,99,173,0.08)",
                boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
                backgroundColor: "rgba(255,255,255,0.96)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  mb: 1.5,
                  gap: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Checkbox
                    checked={selectedUsers.includes(user.Username)}
                    onChange={() => handleSelectUser(user.Username)}
                    color="primary"
                  />
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {user.Username}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#6b778c" }}>
                      {user.Name} {user.Lastname}
                    </Typography>
                  </Box>
                </Box>

                {getStatusChip(user.Status)}
              </Box>

              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Email:</strong> {user.Email}
              </Typography>

              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Accounts:</strong>{" "}
                {user.AssociatedAccounts?.join(", ") || "None"}
              </Typography>

              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Roles:</strong>{" "}
                {user.Roles?.map((r) => r.RoleName).join(", ") || "No Roles"}
              </Typography>

              <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
                <UserActions
                  user={user}
                  onEdit={handleEditUser}
                  onResetPassword={handleResetPassword}
                  onDelete={handleDeleteUser}
                />
              </Box>
            </Paper>
          ))
        ) : (
          <Paper
            sx={{
              p: 2.5,
              borderRadius: "16px",
              border: "1px solid rgba(47,99,173,0.08)",
              boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
              backgroundColor: "rgba(255,255,255,0.96)",
            }}
          >
            <Typography sx={{ color: "text.secondary" }}>
              No users found.
            </Typography>
          </Paper>
        )}
      </Box>

      <UserFormDialog
        open={showForm}
        mode={formMode}
        formData={formData}
        setFormData={setFormData}
        onClose={() => setShowForm(false)}
        onSubmit={handleSubmitUser}
        reloadUsers={loadUsers}
        availableRoles={availableRoles}
        showAssociatedAccount={true}
      />

      <UserResetPasswordDialog
        open={showResetDialog}
        username={userToReset}
        onClose={() => setShowResetDialog(false)}
        onConfirm={confirmResetPassword}
      />

      <UserResetSuccessDialog
        open={!!resetResult}
        username={resetResult?.username}
        password={resetResult?.password}
        onClose={() => setResetResult(null)}
      />

      <UserDeleteDialog
        open={showDeleteDialog}
        username={userToDelete}
        onClose={() => setShowDeleteDialog(false)}
        onDelete={async () => {
          try {
            await api.put(`/users/${userToDelete}/deactivate`);
            setShowDeleteDialog(false);
            loadUsers();
          } catch (err) {
            console.error("Failed to deactivate user:", err);
            alert("Failed to deactivate user.");
          }
        }}
      />
    </Box>
  );
};

export default AllUsersTable;
